import { useMemo } from 'react';
import useMode from './useMode';
import useLocalStorageSettings from './useLocalStorageSettings';

export const useStoreFilters = <
  T extends Record<string, unknown> = Record<string, unknown>,
>(
  page: string
) => {
  const filterKey = useStoreFilterKey(page);
  return (filters: T) => {
    if (filterKey) {
      localStorage.setItem(filterKey, JSON.stringify(filters));
    }
  };
};

export const useStoreFilterKey = (page: string): string | undefined => {
  const mode = useMode();

  return `${mode}.${page}.filters`;
};

const useStoredFilters = <
  T extends Record<string, unknown> = Record<string, unknown>,
>(
  convertFilters: (storedFilters: T) => Record<string, any>,
  page: string,
  defaultFilters: T = {} as T
) => {
  const [stored, setStoredFilters] = useLocalStorageSettings<Partial<T>>(
    page,
    'filters'
  );

  const storedFilters: T = useMemo(() => {
    // ensure that all keys are present or fall back to defaults
    return stored ? { ...defaultFilters, ...stored } : defaultFilters;
  }, [stored, defaultFilters]);

  const queryFilters = useMemo(
    () => convertFilters(storedFilters),
    [convertFilters, storedFilters]
  );

  return {
    storedFilters,
    setStoredFilters,
    queryFilters,
  };
};

export default useStoredFilters;
